import React, { Component } from "react";
import { makeGet, makePost, makeDelete } from "@culturehq/client";
import { Cheer, CheerButton } from "@culturehq/components";

import { ProfileLink } from "app/links";
import { errorService } from "app/utils";

import * as CHQ from "chq";

import getUserName from "app/utils/getUserName";
import CheerListModal from "./CheerListModal";

const cheersLimit = window.innerWidth < 768 ? 3 : 8;
enum Colors {
  darkblue,
  green,
  lightblue,
  yellow
}

const otherCheersFor = (
  cheers: CHQ.Cheer[],
  cheersCount: number,
  cheerForUser: null | CHQ.Cheer,
  isUserProfile: boolean | undefined
) => {
  let otherCheers = cheers;
  if (cheerForUser) {
    otherCheers = cheers.filter(cheer => cheer.id !== cheerForUser.id);
  }

  const buffer = Array(cheersLimit).fill({});
  const otherCount = isUserProfile
    ? Math.min(cheersCount, cheersLimit)
    : Math.min(cheersCount - (cheerForUser ? 1 : 0), cheersLimit);

  return otherCheers.concat(buffer).slice(0, otherCount < 0 ? 0 : otherCount);
};

type CheerUserProps = {
  color: keyof typeof Colors;
  small?: boolean;
  user?: CHQ.User;
};

const CheerUser: React.FC<CheerUserProps> = ({ color, small, user }) => {
  const cheer = <Cheer className={user ? "cheer" : "empty-cheer"} color={color} name={user?.name} small={small} />;

  if (user?.id) {
    return <ProfileLink user={user}>{cheer}</ProfileLink>;
  }
  return cheer;
};

type CheerToggle = (
  _entityId: number,
  _cheerForUser: null | CHQ.Cheer,
  _cheersCount: number
) => void;

type CheerListProps = {
  cheersPath: string;
  entity: CHQ.Cheerable<CHQ.Comment | CHQ.Photo | CHQ.Recognition | CHQ.Story
    | CHQ.UserActivity>;
  onCheerToggle?: CheerToggle;
  small?: boolean;
  showCheerButton?: boolean;
  isUserProfile?: boolean;
};

type CheerListState = {
  cheers: CHQ.Cheer[];
  cheersCount: number;
  cheerForUser: null | CHQ.Cheer;
};

class CheerList extends Component<CheerListProps, CheerListState> {
  private componentIsMounted: boolean;

  private fetchCheersTriggered: boolean;

  static defaultProps = {
    showCheerButton: true
  };

  constructor(props: CheerListProps) {
    super(props);

    this.componentIsMounted = false;
    this.fetchCheersTriggered = false;

    const { cheerForUser, cheersCount } = props.entity;
    this.state = { cheers: [], cheersCount, cheerForUser };
  }

  componentDidMount() {
    this.componentIsMounted = true;
  }

  componentDidUpdate(prevProps: CheerListProps) {
    const { entity } = this.props;

    if (prevProps.entity !== entity) {
      const { cheersCount, cheerForUser } = entity;
      this.setState({ cheersCount, cheerForUser, cheers: [] });
    }
  }

  componentWillUnmount() {
    this.componentIsMounted = false;
  }

  fetchCheers = async () => {
    const { cheersPath } = this.props;
    const { cheers } = await makeGet<{ cheers: CHQ.Cheer[] }>(cheersPath);

    if (this.componentIsMounted) {
      this.setState({ cheers });
    }
  };

  handleCheerToggle = async (cheered: boolean) => {
    const { cheersPath, entity, onCheerToggle } = this.props;
    let cheerForUser: null | CHQ.Cheer = null;

    try {
      if (cheered) {
        const { cheer } = await makePost<{ cheer: CHQ.Cheer }>(cheersPath);
        cheerForUser = cheer;
      } else {
        await makeDelete(cheersPath);
      }
    } catch (error) {
      errorService.error(error);
    }

    this.setState(state => {
      const cheersCount = state.cheersCount + (cheered ? 1 : -1);

      if (onCheerToggle) {
        onCheerToggle(entity.id, cheerForUser, cheersCount);
      }

      return { cheerForUser, cheersCount };
    });
  };

  handleCheerHover = () => {
    if (!this.fetchCheersTriggered) {
      this.fetchCheersTriggered = true;
      this.fetchCheers();
    }
  };

  render() {
    const { cheersPath, small, showCheerButton, isUserProfile } = this.props;
    const { cheersCount, cheerForUser, cheers } = this.state;

    const otherCheers = otherCheersFor(cheers, cheersCount, cheerForUser, isUserProfile);

    return (
      <span className="cheers">
        {showCheerButton && (
          <CheerButton
            cheered={!!cheerForUser}
            onCheerToggle={this.handleCheerToggle}
            name={cheerForUser ? getUserName(cheerForUser.user) : undefined}
            small={small}
          />
        )}
        {otherCheers.length > 0 && (
          <span className="cheers--cheer-list" onMouseEnter={this.handleCheerHover}>
            {otherCheers.map((cheer, index) => (
              <CheerUser
                key={cheer.id || index}
                color={Colors[(1 + index) % 4] as keyof typeof Colors}
                user={cheer.user || {}}
                small={small}
              />
            ))}

            {cheersCount > cheersLimit && (
              <CheerListModal
                cheersCount={cheersCount}
                cheers={cheers}
                cheersPath={cheersPath}
              >
                +{`${cheersCount - cheersLimit}`}
              </CheerListModal>
            )}
          </span>
        )}
        {isUserProfile && otherCheers.length === 0 && (
          <CheerUser
            key="empty-cheers"
            color={Colors[1 % 4] as keyof typeof Colors}
            small={small}
          />
        )}
      </span>
    );
  }
}

export default CheerList;
